import { motion, AnimatePresence } from "framer-motion";
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  Check,
  ChevronUp,
  Crown,
  ListTree,
  Minus,
  Pencil,
  Plus,
  Radar,
  RotateCcw,
  Scan,
  Users,
  Workflow,
  X,
} from "lucide-react";
import { createPortal } from "react-dom";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { cn } from "./cn.js";
import { TEAM_PALETTE } from "./data.js";
import { BackgroundControl } from "./BackgroundControl.js";

const MODES = [
  {
    id: "vertical",
    label: "Top-Down",
    icon: () => <ListTree size={16} strokeWidth={2.2} />,
  },
  {
    id: "horizontal",
    label: "Side Flow",
    icon: () => <Workflow size={16} strokeWidth={2.2} />,
  },
  {
    id: "radial",
    label: "Orbit",
    icon: () => <Radar size={16} strokeWidth={2.2} />,
  },
  {
    id: "inverted",
    label: "Bottom-Up",
    icon: () => (
      <span className="inline-block rotate-180">
        <ListTree size={16} strokeWidth={2.2} />
      </span>
    ),
  },
];

const SIZES = [
  { id: "S", label: "Small cards" },
  { id: "M", label: "Medium cards" },
  { id: "L", label: "Large cards" },
];

const ANCHORS = [
  { id: "left", label: "Left edge" },
  { id: "center", label: "Center" },
  { id: "right", label: "Right edge" },
];

const BUTTON_3D_BASE = {
  transformStyle: "preserve-3d",
  transform: "translateZ(0)",
  transition: "transform 180ms cubic-bezier(.22,1,.36,1), box-shadow 180ms ease",
};

function threeDButton(base, isLight, hover, pressed) {
  const depth = pressed ? 1 : hover ? 4 : 2;
  const offset = pressed ? 0 : hover ? 4 : 2;
  const shadowTop = isLight
    ? "rgba(255,255,255,0.9)"
    : "rgba(255,255,255,0.12)";
  const shadowBottom = isLight
    ? "rgba(15,23,42,0.22)"
    : "rgba(0,0,0,0.65)";
  return {
    ...base,
    transform: `translateZ(0) translateY(-${offset}px) ${pressed ? "scale(0.97)" : hover ? "scale(1.03)" : ""}`,
    boxShadow: `inset 0 1px 0 ${shadowTop}, 0 ${depth}px 0 ${shadowBottom}, 0 ${
      depth + 6
    }px 14px -6px ${isLight ? "rgba(15,23,42,0.28)" : "rgba(0,0,0,0.55)"}`,
  };
}

function DockButton({ onClick, label, children, accent, isLight, C }) {
  const [hover, setHover] = useState(false);
  const [pressed, setPressed] = useState(false);
  return (
    <button
      onClick={onClick}
      aria-label={label}
      title={label}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => { setHover(false); setPressed(false); }}
      onMouseDown={() => setPressed(true)}
      onMouseUp={() => setPressed(false)}
      className="relative flex h-9 w-9 items-center justify-center rounded-xl transition-colors duration-200 border"
      style={{
        color: accent
          ? C.accent
          : hover
            ? isLight
              ? "#0f172a"
              : "#ffffff"
            : isLight
              ? "rgba(15,23,42,0.6)"
              : "rgba(255,255,255,0.6)",
        background: accent
          ? hover
            ? C.accentBgHover
            : C.accentBg
          : hover
            ? isLight
              ? "rgba(15,23,42,0.08)"
              : "rgba(255,255,255,0.09)"
            : isLight
              ? "rgba(255,255,255,0.75)"
              : "rgba(22,24,30,0.7)",
        borderColor: accent
          ? C.accentBgHover
          : isLight
            ? "rgba(15,23,42,0.10)"
            : "rgba(255,255,255,0.11)",
        ...threeDButton(BUTTON_3D_BASE, isLight, hover, pressed),
      }}
    >
      {children}
    </button>
  );
}

function ModeButton({ m, active, onMode, isLight, C }) {
  const [hover, setHover] = useState(false);
  const [pressed, setPressed] = useState(false);
  return (
    <button
      onClick={() => onMode(m.id)}
      aria-pressed={active}
      title={m.label}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => { setHover(false); setPressed(false); }}
      onMouseDown={() => setPressed(true)}
      onMouseUp={() => setPressed(false)}
      className="relative flex items-center gap-1.5 rounded-[10px] px-2.5 py-1.5 transition-colors duration-200 border"
      style={{
        color: active
          ? C.accent
          : hover
            ? isLight
              ? "rgba(15,23,42,0.9)"
              : "rgba(255,255,255,0.9)"
            : isLight
              ? "rgba(15,23,42,0.55)"
              : "rgba(255,255,255,0.55)",
        background: active
          ? C.accentBg
          : hover
            ? isLight
              ? "rgba(15,23,42,0.08)"
              : "rgba(255,255,255,0.09)"
            : "transparent",
        borderColor: active
          ? C.accentBgHover
          : "transparent",
        ...threeDButton(BUTTON_3D_BASE, isLight, hover || active, pressed),
      }}
    >
      {active && (
        <motion.span
          layoutId="dock-pill"
          className="absolute inset-0 rounded-[10px] pointer-events-none"
          style={{
            border: `1px solid ${C.accentBgHover}`,
            boxShadow: `inset 0 0 0 1px ${C.accentBg}`,
          }}
          transition={{ type: "spring", stiffness: 320, damping: 28 }}
        />
      )}
      <span className="relative">{m.icon(active)}</span>
      <span className="relative hidden text-[11px] font-medium tracking-wide sm:block">
        {m.label}
      </span>
    </button>
  );
}

function SizeButton({ s, active, onCardSize, isLight, C }) {
  const [hover, setHover] = useState(false);
  const [pressed, setPressed] = useState(false);
  return (
    <button
      onClick={() => onCardSize(s.id)}
      aria-pressed={active}
      title={s.label}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => { setHover(false); setPressed(false); }}
      onMouseDown={() => setPressed(true)}
      onMouseUp={() => setPressed(false)}
      className="relative flex h-8 w-8 items-center justify-center rounded-[9px] font-mono text-[10.5px] font-semibold transition-colors duration-200 border"
      style={{
        color: active
          ? C.accent
          : hover
            ? isLight
              ? "rgba(15,23,42,0.9)"
              : "rgba(255,255,255,0.9)"
            : isLight
              ? "rgba(15,23,42,0.5)"
              : "rgba(255,255,255,0.5)",
        background: active
          ? C.accentBg
          : hover
            ? isLight
              ? "rgba(15,23,42,0.08)"
              : "rgba(255,255,255,0.09)"
            : "transparent",
        borderColor: active
          ? C.accentBgHover
          : "transparent",
        ...threeDButton(BUTTON_3D_BASE, isLight, hover || active, pressed),
      }}
    >
      {active && (
        <motion.span
          layoutId="size-pill"
          className="absolute inset-0 rounded-[9px] pointer-events-none"
          style={{
            border: `1px solid ${C.accentBgHover}`,
          }}
          transition={{ type: "spring", stiffness: 320, damping: 28 }}
        />
      )}
      <span className="relative">{s.id}</span>
    </button>
  );
}

function AnchorButton({ a, active, onEdgeAnchor, isLight, C }) {
  const [hover, setHover] = useState(false);
  const [pressed, setPressed] = useState(false);
  const Icon = a.id === "left" ? AlignLeft : a.id === "right" ? AlignRight : AlignCenter;
  return (
    <button
      onClick={() => onEdgeAnchor(a.id)}
      aria-pressed={active}
      title={`Connector attaches to ${a.label}`}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => { setHover(false); setPressed(false); }}
      onMouseDown={() => setPressed(true)}
      onMouseUp={() => setPressed(false)}
      className="relative flex h-8 w-8 items-center justify-center rounded-[9px] transition-colors duration-200 border"
      style={{
        color: active
          ? C.accent
          : hover
            ? isLight
              ? "rgba(15,23,42,0.9)"
              : "rgba(255,255,255,0.9)"
            : isLight
              ? "rgba(15,23,42,0.5)"
              : "rgba(255,255,255,0.5)",
        background: active
          ? C.accentBg
          : hover
            ? isLight
              ? "rgba(15,23,42,0.08)"
              : "rgba(255,255,255,0.09)"
            : "transparent",
        borderColor: active
          ? C.accentBgHover
          : "transparent",
        ...threeDButton(BUTTON_3D_BASE, isLight, hover || active, pressed),
      }}
    >
      {active && (
        <motion.span
          layoutId="anchor-pill"
          className="absolute inset-0 rounded-[9px] pointer-events-none"
          style={{ border: `1px solid ${C.accentBgHover}` }}
          transition={{ type: "spring", stiffness: 320, damping: 28 }}
        />
      )}
      <span className="relative">
        <Icon size={14} strokeWidth={2.2} />
      </span>
    </button>
  );
}

function CrownRootButton({ onAddRoot, rootCount, isLight, C }) {
  const [hover, setHover] = useState(false);
  const [pressed, setPressed] = useState(false);
  return (
    <button
      onClick={onAddRoot}
      aria-label="Add a card at the top level"
      title="Add a card at the top (CEO) level"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => { setHover(false); setPressed(false); }}
      onMouseDown={() => setPressed(true)}
      onMouseUp={() => setPressed(false)}
      className="relative flex h-9 items-center gap-1.5 rounded-xl px-2.5 transition-colors duration-200 border"
      style={{
        color: hover
          ? C.accent
          : isLight
            ? "rgba(15,23,42,0.6)"
            : "rgba(255,255,255,0.6)",
        background: hover
          ? isLight
            ? "rgba(15,23,42,0.08)"
            : "rgba(255,255,255,0.09)"
          : isLight
            ? "rgba(255,255,255,0.75)"
            : "rgba(22,24,30,0.7)",
        borderColor: isLight ? "rgba(15,23,42,0.10)" : "rgba(255,255,255,0.11)",
        ...threeDButton(BUTTON_3D_BASE, isLight, hover, pressed),
      }}
    >
      <Crown size={15} strokeWidth={2.1} />
      <Plus size={11} strokeWidth={3} />
      {rootCount > 1 && (
        <span
          className="font-mono text-[10px] tabular-nums"
          style={{
            color: isLight ? "rgba(15,23,42,0.5)" : "rgba(255,255,255,0.45)",
          }}
        >
          {rootCount}
        </span>
      )}
    </button>
  );
}

function Portal({ children }) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  if (!mounted) return null;
  return createPortal(children, document.body);
}

function useAnchoredRect(anchorRef, open) {
  const [rect, setRect] = useState(null);
  useLayoutEffect(() => {
    if (!open || !anchorRef.current) {
      setRect(null);
      return;
    }
    const measure = () => {
      const r = anchorRef.current?.getBoundingClientRect?.();
      if (!r) return;
      setRect({
        left: r.left + window.scrollX,
        top: r.top + window.scrollY,
        right: r.right + window.scrollX,
        bottom: r.bottom + window.scrollY,
        width: r.width,
        height: r.height,
        vw: window.innerWidth,
        vh: window.innerHeight,
      });
    };
    measure();
    const timeout = window.setTimeout(measure, 16);
    window.addEventListener("scroll", measure, true);
    window.addEventListener("resize", measure);
    return () => {
      window.clearTimeout(timeout);
      window.removeEventListener("scroll", measure, true);
      window.removeEventListener("resize", measure);
    };
  }, [open, anchorRef]);
  return rect;
}

function TeamsButton({
  teams,
  counts,
  highlight,
  onHighlight,
  onAdd,
  onRemove,
  isLight,
  C,
}) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState("");
  const [color, setColor] = useState(TEAM_PALETTE[0]);
  const [error, setError] = useState(false);
  const [hover, setHover] = useState(false);
  const [pressed, setPressed] = useState(false);
  const inputRef = useRef(null);
  const buttonRef = useRef(null);
  const popupRef = useRef(null);
  const rect = useAnchoredRect(buttonRef, open);

  useEffect(() => {
    if (adding) inputRef.current?.focus();
  }, [adding]);

  useEffect(() => {
    if (!open) {
      setEditing(false);
      setAdding(false);
      setDraft("");
    }
  }, [open]);

  useEffect(() => {
    const handler = (e) => {
      const btnEl = buttonRef.current;
      const popEl = popupRef.current;
      if (btnEl && btnEl.contains(e.target)) return;
      if (popEl && popEl.contains(e.target)) return;
      setOpen(false);
    };
    if (open) {
      window.addEventListener("mousedown", handler);
      return () => window.removeEventListener("mousedown", handler);
    }
  }, [open]);

  const commit = () => {
    if (!draft.trim()) {
      setAdding(false);
      return;
    }
    if (onAdd(draft, color)) {
      setDraft("");
      setError(false);
      setColor(TEAM_PALETTE[(TEAM_PALETTE.indexOf(color) + 1) % TEAM_PALETTE.length]);
      inputRef.current?.focus();
    } else {
      setError(true);
      setTimeout(() => setError(false), 1200);
    }
  };

  const popupBg = isLight ? "rgba(255,255,255,0.96)" : "rgba(14,15,19,0.95)";
  const popupBorder = isLight ? "rgba(15,23,42,0.1)" : "rgba(255,255,255,0.1)";
  const popupShadow = isLight
    ? "0 22px 60px -14px rgba(15,23,42,0.35), inset 0 1px 0 rgba(255,255,255,0.95)"
    : "0 22px 60px -14px rgba(0,0,0,0.85), inset 0 1px 0 rgba(255,255,255,0.12)";

  const popupPosition = useMemo(() => {
    if (!rect) return { top: -9999, left: "50%", translateX: "-50%" };
    const panelW = Math.min(rect.vw * 0.92, 560);
    const panelH = 420;
    let left = rect.left + rect.width / 2 - panelW / 2;
    const minLeft = Math.max(8, window.scrollX + 8);
    const maxLeft = window.scrollX + rect.vw - panelW - 8;
    left = Math.min(Math.max(left, minLeft), maxLeft);
    const top = rect.top - panelH - 10;
    return { top, left, panelW, fixed: true };
  }, [rect]);

  return (
    <>
      <div className="relative">
        <motion.button
          ref={buttonRef}
          onClick={() => setOpen((v) => !v)}
          aria-label="Teams"
          title="Teams"
          aria-haspopup="dialog"
          aria-expanded={open}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => { setHover(false); setPressed(false); }}
          onMouseDown={() => setPressed(true)}
          onMouseUp={() => setPressed(false)}
          whileTap={{ scale: 0.97 }}
          className="relative flex h-9 items-center justify-center gap-1 rounded-xl px-2.5 text-[10.5px] font-semibold shrink-0"
          style={{
            color: open || highlight ? C.accent : isLight ? "#0f172a" : "#ffffff",
            background: open || highlight ? C.accentBg : "transparent",
            border: `1px solid ${isLight ? "rgba(15,23,42,0.08)" : "rgba(255,255,255,0.1)"}`,
            ...threeDButton(BUTTON_3D_BASE, isLight, hover, pressed),
          }}
        >
          <Users size={15} strokeWidth={2.2} />
          <span className="font-mono text-[10px] tabular-nums">{teams.length}</span>
        </motion.button>
      </div>
      {rect && (
        <Portal>
          <AnimatePresence>
            {open && (
              <motion.div
                ref={popupRef}
                key="teams-popup"
                initial={{ y: 10, opacity: 0, scale: 0.96 }}
                animate={{ y: 0, opacity: 1, scale: 1 }}
                exit={{ y: 10, opacity: 0, scale: 0.96 }}
                transition={{ type: "spring", stiffness: 260, damping: 24 }}
                className="fixed z-[99999] min-w-[320px] rounded-2xl border p-3 backdrop-blur-xl"
                style={{
                  top: popupPosition.top,
                  left: popupPosition.left,
                  width: popupPosition.panelW,
                  maxWidth: "92vw",
                  maxHeight: "calc(100vh - 5rem)",
                  overflowY: "auto",
                  background: popupBg,
                  borderColor: popupBorder,
                  boxShadow: popupShadow,
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="mb-2 flex items-center justify-between">
                  <span
                    className="font-mono text-[8.5px] uppercase tracking-[0.25em]"
                    style={{ color: isLight ? "rgba(15,23,42,0.45)" : "rgba(255,255,255,0.35)" }}
                  >
                    Teams
                  </span>
                  <button
                    onClick={() => {
                      setEditing((v) => !v);
                      setAdding(false);
                    }}
                    aria-pressed={editing}
                    title={editing ? "Done editing teams" : "Edit teams"}
                    className="flex h-6 w-6 items-center justify-center rounded-lg"
                    style={
                      editing
                        ? { background: C.accentBg, color: C.accent }
                        : { color: isLight ? "rgba(15,23,42,0.4)" : "rgba(255,255,255,0.4)" }
                    }
                    onMouseEnter={(e) => {
                      if (!editing) {
                        e.currentTarget.style.background = isLight
                          ? "rgba(15,23,42,0.06)"
                          : "rgba(255,255,255,0.07)";
                        e.currentTarget.style.color = isLight ? "rgba(15,23,42,0.9)" : "#ffffff";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!editing) {
                        e.currentTarget.style.background = "transparent";
                        e.currentTarget.style.color = isLight
                          ? "rgba(15,23,42,0.4)"
                          : "rgba(255,255,255,0.4)";
                      }
                    }}
                  >
                    {editing ? <Check size={12} strokeWidth={2.6} /> : <Pencil size={11} />}
                  </button>
                </div>

                <div className="flex flex-wrap items-center gap-1.5">
                  <AnimatePresence initial={false}>
                    {teams.map((t) => {
                      const active = highlight === t.name;
                      const count = counts.get(t.name) ?? 0;
                      return (
                        <motion.div
                          key={t.name}
                          layout
                          initial={{ opacity: 0, scale: 0.7 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.6 }}
                          transition={{ type: "spring", stiffness: 380, damping: 28 }}
                          className={cn(
                            "flex items-center gap-1.5 rounded-full border py-1 pl-2.5 text-[10.5px] transition-colors duration-200",
                            editing ? "pr-1" : "pr-2.5",
                            active
                              ? "border-transparent text-black"
                              : isLight
                              ? "border-slate-200 text-slate-500 hover:border-slate-300 hover:text-slate-800"
                              : "border-white/[0.09] text-white/55 hover:border-white/25 hover:text-white/90"
                          )}
                          style={active ? { background: t.color } : undefined}
                        >
                          <button
                            onClick={() => onHighlight(active ? null : t.name)}
                            aria-pressed={active}
                            title={active ? `Clear ${t.name} highlight` : `Highlight ${t.name}`}
                            className="flex items-center gap-1.5"
                          >
                            <span
                              className="h-1.5 w-1.5 rounded-full"
                              style={{
                                background: active ? "#000" : t.color,
                                boxShadow: active ? undefined : `0 0 6px ${t.color}80`,
                              }}
                            />
                            {t.name}
                            <span
                              className={cn(
                                "font-mono text-[9px]",
                                active
                                  ? "text-black/55"
                                  : isLight
                                  ? "text-slate-400"
                                  : "text-white/30"
                              )}
                            >
                              {count}
                            </span>
                          </button>
                          {editing && (
                            <button
                              onClick={() => onRemove(t.name)}
                              disabled={teams.length <= 1}
                              aria-label={`Delete team ${t.name}`}
                              title={
                                teams.length <= 1
                                  ? "At least one team is required"
                                  : `Delete ${t.name}${count ? ` — ${count} member(s) move to ${teams[0].name === t.name ? teams[1]?.name : teams[0].name}` : ""}`
                              }
                              className={cn(
                                "flex h-4 w-4 items-center justify-center rounded-full transition-colors",
                                active
                                  ? "text-black/50 hover:bg-black/15 hover:text-black"
                                  : isLight
                                  ? "text-slate-400 hover:bg-[rgba(239,68,68,0.18)] hover:text-[#ef4444]"
                                  : "text-white/35 hover:bg-[rgba(255,107,107,0.18)] hover:text-[#ff8f8f]",
                                teams.length <= 1 && "cursor-not-allowed opacity-30"
                              )}
                            >
                              <X size={10} strokeWidth={2.6} />
                            </button>
                          )}
                        </motion.div>
                      );
                    })}
                  </AnimatePresence>

                  {adding ? (
                    <motion.div
                      layout
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className={cn(
                        "flex items-center gap-1.5 rounded-full border py-1 pl-1.5 pr-1 transition-colors",
                        error ? "border-[#ef4444]" : ""
                      )}
                      style={!error ? { borderColor: C.accentSoftEdge } : undefined}
                    >
                      <button
                        onClick={() =>
                          setColor(TEAM_PALETTE[(TEAM_PALETTE.indexOf(color) + 1) % TEAM_PALETTE.length])
                        }
                        title="Cycle team color"
                        aria-label="Cycle team color"
                        className="h-3 w-3 shrink-0 rounded-full transition-transform hover:scale-125"
                        style={{ background: color, boxShadow: `0 0 7px ${color}90` }}
                      />
                      <input
                        ref={inputRef}
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") commit();
                          if (e.key === "Escape") {
                            setAdding(false);
                            setDraft("");
                          }
                        }}
                        placeholder={error ? "Name taken" : "Team name"}
                        maxLength={18}
                        className="w-[92px] bg-transparent text-[10.5px] outline-none"
                        style={{
                          color: isLight ? "rgba(15,23,42,0.9)" : "rgba(255,255,255,0.9)",
                        }}
                      />
                      <button
                        onClick={commit}
                        aria-label="Save team"
                        className="flex h-4 w-4 items-center justify-center rounded-full"
                        style={{ color: C.accent }}
                      >
                        <span
                          className="flex h-4 w-4 items-center justify-center rounded-full"
                          onMouseEnter={(e) => (e.currentTarget.style.background = C.accentBgHover)}
                          onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                        >
                          <Check size={11} strokeWidth={3} />
                        </span>
                      </button>
                      <button
                        onClick={() => {
                          setAdding(false);
                          setDraft("");
                        }}
                        aria-label="Cancel"
                        className="flex h-4 w-4 items-center justify-center rounded-full"
                        style={{ color: isLight ? "rgba(15,23,42,0.4)" : "rgba(255,255,255,0.4)" }}
                      >
                        <X size={10} strokeWidth={2.6} />
                      </button>
                    </motion.div>
                  ) : (
                    <motion.button
                      layout
                      onClick={() => {
                        setAdding(true);
                        setEditing(true);
                      }}
                      title="Add a team"
                      aria-label="Add a team"
                      className="flex items-center gap-1 rounded-full border border-dashed px-2 py-1 text-[10.5px] transition-colors shrink-0"
                      style={{
                        borderColor: isLight ? "rgba(15,23,42,0.18)" : "rgba(255,255,255,0.2)",
                        color: isLight ? "rgba(15,23,42,0.45)" : "rgba(255,255,255,0.45)",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = C.accentSoftEdge;
                        e.currentTarget.style.color = C.accent;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = isLight
                          ? "rgba(15,23,42,0.18)"
                          : "rgba(255,255,255,0.2)";
                        e.currentTarget.style.color = isLight
                          ? "rgba(15,23,42,0.45)"
                          : "rgba(255,255,255,0.45)";
                      }}
                    >
                      <Plus size={11} strokeWidth={2.6} />
                      Add
                    </motion.button>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </Portal>
      )}
    </>
  );
}

function ToggleChevron({ expanded, onToggle, isLight, C }) {
  const [hover, setHover] = useState(false);
  const [pressed, setPressed] = useState(false);
  return (
    <motion.button
      aria-label={expanded ? "Hide dock controls" : "Show dock controls"}
      title={expanded ? "Hide dock controls" : "Show dock controls"}
      onClick={onToggle}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => { setHover(false); setPressed(false); }}
      onMouseDown={() => setPressed(true)}
      onMouseUp={() => setPressed(false)}
      initial={{ y: 0, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ type: "spring", stiffness: 160, damping: 20, delay: 0.55 }}
      className={cn(
        "relative flex h-11 w-11 items-center justify-center rounded-2xl border backdrop-blur-xl",
        "overflow-hidden select-none"
      )}
      style={{
        background: isLight
          ? "rgba(255,255,255,0.62)"
          : "rgba(14,15,19,0.62)",
        borderColor: isLight
          ? "rgba(15,23,42,0.12)"
          : "rgba(255,255,255,0.12)",
        color: C.accent,
        boxShadow: isLight
          ? "0 16px 44px -14px rgba(15,23,42,0.35), inset 0 1px 0 rgba(255,255,255,0.9)"
          : "0 22px 60px -14px rgba(0,0,0,0.75), inset 0 1px 0 rgba(255,255,255,0.14)",
        ...threeDButton(BUTTON_3D_BASE, isLight, hover, pressed),
      }}
    >
      <span
        className="pointer-events-none absolute inset-0 rounded-2xl"
        style={{
          background: `radial-gradient(circle at 50% 0%, ${C.aura1 || C.accent}33, transparent 60%)`,
          opacity: hover ? 1 : 0.55,
        }}
      />
      <motion.span
        animate={{ rotate: expanded ? 180 : 0, y: expanded ? 1 : 0 }}
        transition={{ type: "spring", stiffness: 260, damping: 22 }}
        className="relative"
      >
        <ChevronUp size={20} strokeWidth={2.4} />
      </motion.span>
    </motion.button>
  );
}

export function Dock({
  mode,
  onMode,
  zoom,
  onZoomStep,
  onFit,
  onReset,
  cardSize,
  onCardSize,
  edgeAnchor = "center",
  onEdgeAnchor,
  background,
  onBackground,
  onNotify,
  rootCount,
  onAddRoot,
  teams,
  teamCounts,
  teamHighlight,
  onTeamHighlight,
  onTeamAdd,
  onTeamRemove,
  expanded,
  onToggleExpanded,
  isLight,
  C,
}) {
  const [pct, setPct] = useState(Math.round(zoom.get() * 100));

  useEffect(() => {
    const unsubscribe = zoom.on("change", (v) => {
      setPct(typeof v === "number" ? Math.round(v * 100) : 0);
    });
    return () => unsubscribe();
  }, [zoom]);

  const surfaceBg = isLight ? "rgba(255,255,255,0.82)" : "rgba(14,15,19,0.78)";
  const surfaceBorder = isLight ? "rgba(15,23,42,0.12)" : "rgba(255,255,255,0.1)";
  const surfaceShadow = isLight
    ? "0 30px 70px -20px rgba(15,23,42,0.35), inset 0 1px 0 rgba(255,255,255,0.9)"
    : "0 34px 80px -20px rgba(0,0,0,0.8), inset 0 1px 0 rgba(255,255,255,0.12)";
  const innerPanelBg = isLight ? "rgba(15,23,42,0.05)" : "rgba(255,255,255,0.05)";
  const dividerBg = isLight ? "rgba(15,23,42,0.10)" : "rgba(255,255,255,0.1)";
  const pctText = isLight ? "rgba(15,23,42,0.7)" : "rgba(255,255,255,0.65)";

  return (
    <>
      <AnimatePresence>
        {expanded && (
          <motion.div
            key="dock-toolbar"
            initial={{ y: 80, opacity: 0, scale: 0.94, filter: "blur(10px)" }}
            animate={{ y: -14, opacity: 1, scale: 1, filter: "blur(0px)" }}
            exit={{ y: 80, opacity: 0, scale: 0.94, filter: "blur(10px)" }}
            transition={{
              type: "spring",
              stiffness: 180,
              damping: 24,
              mass: 0.78,
            }}
            className="pointer-events-none fixed bottom-20 left-0 right-0 z-[70] mx-auto w-[min(96vw,1400px)] px-6"
          >
            <div
              className={cn(
                "pointer-events-auto mx-auto flex w-fit max-w-full flex-nowrap items-center gap-2 overflow-x-auto overscroll-x-contain rounded-2xl p-2 backdrop-blur-2xl [scrollbar-width:none]",
                "dock-rail touch-pan-x"
              )}
              style={{
                background: surfaceBg,
                border: `1px solid ${surfaceBorder}`,
                boxShadow: surfaceShadow,
              }}
            >
              <style>{`.dock-rail::-webkit-scrollbar{display:none}`}</style>
              <div
                className="flex items-center gap-1 rounded-xl p-1"
                style={{ background: innerPanelBg }}
              >
                {MODES.map((m) => (
                  <ModeButton
                    key={m.id}
                    m={m}
                    active={mode === m.id}
                    onMode={onMode}
                    isLight={isLight}
                    C={C}
                  />
                ))}
              </div>

              <div className="h-7 w-px rounded-full" style={{ background: dividerBg }} />

              <div
                className="flex items-center gap-1 rounded-xl p-1"
                style={{ background: innerPanelBg }}
              >
                {SIZES.map((s) => (
                  <SizeButton
                    key={s.id}
                    s={s}
                    active={cardSize === s.id}
                    onCardSize={onCardSize}
                    isLight={isLight}
                    C={C}
                  />
                ))}
              </div>

              <div className="h-7 w-px rounded-full" style={{ background: dividerBg }} />

              <div
                className="flex items-center gap-1 rounded-xl p-1"
                style={{ background: innerPanelBg }}
                title="Connector anchor point on each card"
              >
                <span
                  className="px-1 font-mono text-[9px] uppercase tracking-[0.16em]"
                  style={{ color: isLight ? "rgba(15,23,42,0.4)" : "rgba(255,255,255,0.35)" }}
                >
                  Anchor
                </span>
                {ANCHORS.map((a) => (
                  <AnchorButton
                    key={a.id}
                    a={a}
                    active={edgeAnchor === a.id}
                    onEdgeAnchor={onEdgeAnchor}
                    isLight={isLight}
                    C={C}
                  />
                ))}
              </div>

              <div className="h-7 w-px rounded-full" style={{ background: dividerBg }} />

              <DockButton
                onClick={() => onZoomStep(-1)}
                label="Zoom out"
                isLight={isLight}
                C={C}
              >
                <Minus size={15} />
              </DockButton>
              <span
                className="w-12 text-center font-mono text-[11px] tabular-nums font-semibold"
                style={{ color: pctText }}
              >
                {pct}%
              </span>
              <DockButton
                onClick={() => onZoomStep(1)}
                label="Zoom in"
                isLight={isLight}
                C={C}
              >
                <Plus size={15} />
              </DockButton>
              <DockButton
                onClick={onFit}
                label="Fit chart to screen"
                accent
                isLight={isLight}
                C={C}
              >
                <Scan size={15} />
              </DockButton>

              <div className="h-7 w-px rounded-full" style={{ background: dividerBg }} />

              <CrownRootButton
                onAddRoot={onAddRoot}
                rootCount={rootCount}
                isLight={isLight}
                C={C}
              />

              <TeamsButton
                teams={teams}
                counts={teamCounts}
                highlight={teamHighlight}
                onHighlight={onTeamHighlight}
                onAdd={onTeamAdd}
                onRemove={onTeamRemove}
                isLight={isLight}
                C={C}
              />

              <BackgroundControl
                background={background}
                onChange={onBackground}
                onNotify={onNotify}
                isLight={isLight}
                C={C}
              />

              <DockButton
                onClick={onReset}
                label="Center organization"
                isLight={isLight}
                C={C}
              >
                <RotateCcw size={14} />
              </DockButton>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="pointer-events-none fixed inset-x-0 bottom-5 z-[70] flex justify-center overflow-visible px-6">
        <div className="pointer-events-auto">
          <ToggleChevron
            expanded={expanded}
            onToggle={onToggleExpanded}
            isLight={isLight}
            C={C}
          />
        </div>
      </div>
    </>
  );
}
